import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight, X, ClipboardCheck } from 'lucide-react';
import { rosterApi } from '../services/api';
import { ResolveShiftDialog } from './ResolveShiftDialog';

/** Compact red banner listing shifts flagged missed=true in the last 24h.
 *  Only renders when there ARE missed shifts. Auto-polls every 5 minutes. */
export function MissedShiftsBanner() {
  const [missed, setMissed] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await rosterApi.listMissed(24);
      setMissed(Array.isArray(data) ? data.filter(s => !s.missed_resolution) : []);
    } catch {
      // silent — not worth spamming toasts
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (dismissed || missed.length === 0) return null;

  const first = missed[0];
  const extras = missed.length - 1;
  const time = (() => {
    try {
      return new Date(first.start_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return first.start_at; }
  })();

  return (
    <div
      className="bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-lg p-4 flex items-start gap-3 mb-4"
      data-testid="missed-shifts-banner"
      role="alert"
    >
      <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-heading font-semibold text-red-900">
            {missed.length === 1 ? 'Missed shift' : `${missed.length} missed shifts`}
          </h3>
          <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium">last 24h</span>
        </div>
        <p className="text-sm text-red-800 mt-1">
          <span className="font-medium">{first.worker_name || 'A worker'}</span> did not clock in for
          the {time} shift with <span className="font-medium">{first.client_name || 'a participant'}</span>.
          {extras > 0 && (
            <span className="text-red-600"> + {extras} more.</span>
          )}
        </p>
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <button
            type="button"
            onClick={() => setResolveTarget(first)}
            className="inline-flex items-center gap-1 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-2.5 py-1.5 transition-colors"
            data-testid="missed-shifts-resolve-btn"
          >
            <ClipboardCheck className="h-3.5 w-3.5" /> Resolve
          </button>
          <Link
            to="/roster"
            className="text-sm text-red-700 hover:text-red-900 font-medium inline-flex items-center gap-1"
            data-testid="missed-shifts-view-btn"
          >
            Open roster <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
      <button
        onClick={() => setDismissed(true)}
        className="text-red-400 hover:text-red-700 shrink-0"
        aria-label="Dismiss"
        data-testid="missed-shifts-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <ResolveShiftDialog shift={resolveTarget} open={!!resolveTarget} onOpenChange={(o) => !o && setResolveTarget(null)} onResolved={load} />
    </div>
  );
}
